import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ImgCheck from '../../../static/image/checkmark-256.png';

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${(props) => (props.gradient === 'white'
    ? 'linear-gradient(to bottom, rgba(255,255,255,0) 50%, rgba(255,255,255,1) 100%)'
    : 'linear-gradient(to bottom,rgba(0, 0, 0, 0) 70%,rgba(0, 0, 0, 0.5) 85%,rgba(0, 0, 0, 1) 100%)')};
  z-index: 1;             
  pointer-events: none;  
`;

const StyledTopic = styled.div`     
  width: 100%;
  font-size: ${(props) => (props.titleLarge
    ? `${props.containerWidth / (['ko', 'ja', 'zh'].includes(props.language) ? 13 : 14)}px !important`
    : props.variant === 'newBooksByUser'
      ? `${props.containerWidth / 17}px !important`
      : `${props.containerWidth / (['ko', 'ja', 'zh'].includes(props.language) ? 15 : 16)}px`)
};
  letter-spacing: -0.9px;
  ${(props) => (['zh', 'ja', 'th'].includes(props.language) ? 'word-break: break-word;' : 'word-break: keep-all;')}; 
  white-space: normal;
  font-family: ${(props) => (
    props.language === 'ja' ? 'NotoSansJP400, sans-serif'
      : props.language === 'zh' ? 'NotoSansSC400, sans-serif'
        : props.language === 'th' ? 'NotoSansTH400, sans-serif'
          : props.language === 'ar' ? 'Noto Sans Arabic, sans-serif'
            : props.language === 'vi' ? 'Roboto, sans-serif'
              : 'Noto Sans CJK KR, sans-serif'
  )};
  color: ${(props) => props.topicColor || 'white'};
  text-align: ${({ language }) => (language === 'ar' ? 'right' : 'left')};
  font-weight: ${(props) => (props.titleLarge ? 600 : 400)};
  z-index: 4;
  position: absolute;
  bottom: ${(props) => (props.titleBottom ? props.titleBottom : (props.from === 'page' ? '15px' : '6px'))};
  padding: ${(props) => props.containerWidth / 15}px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: ${(props) => (props.from === 'page' ? 1.5 : 1.3)};
  display: -webkit-box;
  -webkit-line-clamp: 2;          
  -webkit-box-orient: vertical;    
  overflow: hidden;               
  text-overflow: ellipsis;
  text-shadow: ${(props) => (props.from !== 'page' && '0 1px 2px rgba(0, 0, 0, 0.4)')}; 
  max-height: ${(props) => (
    props.customMaxHeight
      ? props.customMaxHeight
      : props.userLanguage === 'ko'
        ? 'calc(1.3em * 2.7)'
        : 'calc(1.3em * 2.7)'
  )} ;
`;

const CoverImage = ({ node, onClick, from, borderRadius, isSelected,
  variant, topicColor, titleSize, titleBottom, customMaxHeight,
  gradient, titleLarge, userLanguage, type }) => {
  const { imageURL, showTopicOnImage, topic, language, creator } = node || {};
  const containerRef = useRef(null);

  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        const minWidth = width < 91 ? 90 : width < 131 ? 130 : width < 151 ? 150 : width;
        setContainerWidth(minWidth);
      }
    };

    // DOM이 완전히 렌더링된 후에 초기값 설정
    const timer = setTimeout(updateWidth, 0); // 비동기적으로 처리

    window.addEventListener('resize', updateWidth);

    return () => {
      clearTimeout(timer); // 타이머 정리
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        aspectRatio: '1', // 1:1 비율 설정
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `url(${imageURL || creator?.photo})`,
        backgroundSize: from === 'fullScreen' ? 'contain' : 'cover',
        backgroundPosition: 'center',
        overflow: 'hidden',
        borderRadius: borderRadius && borderRadius,
        height: from === 'fullScreen' ? '100vh' : 'auto',
        backgroundRepeat: from === 'fullScreen' ? 'no-repeat' : '',
      }}
      onClick={onClick}
    >
      {isSelected && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)', // 검정 반투명 배경
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={ImgCheck}
            alt="Selected"
            style={{
              width: '40%',
              height: '40%',
            }}
          />
        </div>
      )}
      <GradientOverlay gradient={gradient} />
      {showTopicOnImage && (
        <StyledTopic
          language={language}
          containerWidth={containerWidth}
          topicColor={topicColor || (from === 'page' ? 'black' : 'white')}
          from={from}
          variant={variant || type}
          titleSize={titleSize}
          titleBottom={titleBottom}
          customMaxHeight={customMaxHeight}
          titleLarge={titleLarge}
          userLanguage={userLanguage}
        >
          {topic}
        </StyledTopic>
      )}
    </div>
  );
};

CoverImage.propTypes = {
  node: PropTypes.shape({
    imageURL: PropTypes.string.isRequired,
    topic: PropTypes.string.isRequired,
    showTopicOnImage: PropTypes.bool,
    language: PropTypes.string,
  }),
  onClick: PropTypes.func,
  from: PropTypes.string,
  borderRadius: PropTypes.string,
  isSelected: PropTypes.bool,
  variant: PropTypes.string,
  type: PropTypes.string,
  titleSize: PropTypes.string,
  titleBottom: PropTypes.string,
  customMaxHeight: PropTypes.string,
  gradient: PropTypes.string,
  titleLarge: PropTypes.bool,
  topicColor: PropTypes.string,
  userLanguage: PropTypes.string,
};

CoverImage.defaultProps = {
  node: {
  },
  onClick: () => {},
  from: '',
  borderRadius: '',
  isSelected: false,
  variant: '',
  type: '',
  titleSize: 'normal',
  titleBottom: '',
  customMaxHeight: '',
  gradient: '',
  titleLarge: false,
  topicColor: '',
  userLanguage: '',
};

export default CoverImage;
